/* import __COLOCATED_TEMPLATE__ from './email-follow-up.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type { FinSetupBehaviorSaveParams } from 'embercom/lib/operator/fin/types';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type Profile from 'embercom/models/ai-agent/profile';

type EmailFollowUpOption = 'follow_up' | 'no_follow_up';

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const DEFAULT_FOLLOW_UP_SECONDS = 24 * HOUR;
const MIN_EMAIL_FOLLOW_UP_DURATION = HOUR;
const MAX_EMAIL_FOLLOW_UP_DURATION = 7 * DAY;

interface Args {
  accordion: $TSFixMe;
  ruleset: $TSFixMe;
  partialSave: (params: FinSetupBehaviorSaveParams) => void;
}

interface Signature {
  Args: Args;
}

export default class EmailFollowUp extends Component<Signature> {
  @service declare intl: IntlService;
  @tracked selectedOption: EmailFollowUpOption =
    (this.behavior.emailFollowUpType as EmailFollowUpOption) || 'follow_up';

  get ruleset() {
    return this.args.ruleset;
  }

  get behavior(): Profile {
    return this.ruleset.rulesetLinks.firstObject.object;
  }

  get options() {
    return [
      {
        value: 'no_follow_up' as const,
        translationKey:
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.option-no-follow-up',
        subtitleTranslationKey:
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.option-no-follow-up-subtitle',
      },
      {
        value: 'follow_up' as const,
        translationKey:
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.option-follow-up',
        subtitleTranslationKey:
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.option-follow-up-subtitle',
      },
    ];
  }

  get selectedLabel() {
    switch (this.selectedOption) {
      case 'follow_up':
        return this.intl.t(
          'operator.fin.setup.setup-and-go-live.email-follow-up.summary-follow-up',
        );
      case 'no_follow_up':
        return this.intl.t(
          'operator.fin.setup.setup-and-go-live.email-follow-up.summary-no-follow-up',
        );
    }
  }

  get effectiveEmailFollowUpTimer(): number {
    return this.behavior.emailFollowUpTimer ?? DEFAULT_FOLLOW_UP_SECONDS;
  }

  get emailFollowUpDurationSummary(): string {
    let timer = this.effectiveEmailFollowUpTimer;
    let days = Math.floor(timer / DAY);
    let hours = Math.floor((timer % DAY) / HOUR);

    let parts: string[] = [];
    if (days > 0) {
      parts.push(
        this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.duration-summary.days',
          { count: days },
        ),
      );
    }
    if (hours > 0) {
      parts.push(
        this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.duration-summary.hours',
          { count: hours },
        ),
      );
    }

    if (parts.length === 0) {
      return this.intl.t(
        'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.duration-summary.empty',
      );
    }

    return this.intl.t(
      'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.duration-summary.after',
      { duration: parts.join(', ') },
    );
  }

  get minEmailFollowUpDuration() {
    return MIN_EMAIL_FOLLOW_UP_DURATION;
  }

  get maxEmailFollowUpDuration() {
    return MAX_EMAIL_FOLLOW_UP_DURATION;
  }

  get shouldShowPreview() {
    return this.selectedOption !== 'no_follow_up';
  }

  get previewText() {
    return this.intl.t(
      'operator.workflows.visual-builder.pass-to-fin.settings.email-follow-up.preview-text',
    );
  }

  @action
  setSelectedOption(option: EmailFollowUpOption) {
    this.selectedOption = option;
  }

  @action
  updateEmailFollowUpDuration(durationInSeconds: number) {
    this.behavior.emailFollowUpTimer = durationInSeconds;
  }

  @action
  async updateEmailFollowUpConfig() {
    this.args.partialSave({
      email_follow_up_type: this.selectedOption,
      ...(this.behavior.emailFollowUpTimer != null && {
        email_follow_up_timer: this.behavior.emailFollowUpTimer,
      }),
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::EmailFollowUp': typeof EmailFollowUp;
    'operator/fin/setup/sections/email-follow-up': typeof EmailFollowUp;
  }
}
